// The reply follows the agent's own steps (2026-10-30, docs/contact-view.md
// "The reply follows the steps"; docs/decisions/2026-10-30-agent-draft-own-steps.md).
//
// With the agent's draft as the main draft, the steps shown are the agent's
// own (modelMeta.stepsSource 'agent'), and the reply's refund amount is the
// step's: the server filled it from the step's breakdown at generation
// (params.replyRefundFilled). When the person changes the step (items,
// quantities, reason code, Return / Scrap, who pays) the panel fills it
// again, keeping every other edit (refilledReply()); a change that switches
// the reply's template (scrap <-> return, who pays) offers that outcome's
// reply through the house template switch ("Replace your reply?"), from
// params.replyByKind. Before Send, a dollar amount in a refund sentence that
// is no amount on a step, and "no return" on an RMA (or the reverse), are
// warnings with a fix where there is one (@cxhub/shared's
// replyStepWarnings()). They never block. Pure: node --test covers it.

import { refundsFromSteps, replyStepWarnings, replaceRefundAmount, setRefundAmountLines, hasRefundBreakdownLines, REPLY_STEP_WARNING_CODES, itemStepOf, replyItemsWarning } from '@cxhub/shared'

const round2 = (n) => Math.round(Number(n) * 100) / 100
const num = (v) => {
  if (v == null || v === '') return null
  const n = Number(v)
  return Number.isFinite(n) ? round2(n) : null
}

// Whether the steps shown are the agent's own.
export function agentStepsShown(suggestion) {
  return suggestion?.modelMeta?.stepsSource === 'agent'
}

// The refund steps as they stand now, with the person's edits:
// refundsFromSteps() of the stored steps, then per return step its
// disposition (the person's Return / Scrap, else the latest check's, else the
// stored one), who pays (the latest check's, else the stored one) and its
// amounts (the items' total the panel computed for the current selection,
// originalFor(index), else the latest check's; the deduction 0 for scrap, a
// company-paid label or the customer's own label, else the person's or the
// check's). An eBay refund's amount follows its Full / Partial input.
export function liveRefunds(actions = [], { inputs = {}, checkStates = {}, unchecked = [], originalFor = null } = {}) {
  return refundsFromSteps(actions, { unchecked }).map((r) => {
    const a = actions[r.index] ?? {}
    const input = inputs?.[r.index] ?? {}
    const res = checkStates?.[r.index]?.result ?? null
    if (r.kind === 'return') {
      const disposition = typeof input.dispositionOverride === 'boolean'
        ? (input.dispositionOverride ? 'return' : 'scrap')
        : (typeof res?.disposition === 'boolean' ? (res.disposition ? 'return' : 'scrap') : r.disposition)
      const payer = res?.returnShippingPaidBy ?? a.params?.dispositionCheck?.returnShippingPaidBy ?? null
      const live = typeof originalFor === 'function' ? num(originalFor(r.index)) : null
      const original = live ?? num(input.itemsTotal) ?? num(res?.refund?.original) ?? r.original
      const noDeduction = disposition === 'scrap' || payer === 'company' || input.shippingPaymentOption === 'customerShipsOwn'
      const deduction = noDeduction ? 0 : (num(input.shippingDeduction) ?? num(res?.refund?.deduction) ?? r.deduction)
      const final = original != null && deduction != null ? round2(original - deduction) : (num(res?.refund?.final) ?? r.final)
      return { ...r, disposition, payer, original, deduction, final }
    }
    if (r.kind === 'refund' && (input.refundType === 'full' || input.refundType === 'partial')) {
      const amount = input.refundType === 'full' ? num(a.params?.orderTotal) : num(input.amount)
      return { ...r, original: amount, final: amount }
    }
    return r
  })
}

// The one refund the reply's amount follows: the only refund step with a
// final amount, else null.
export function replyRefundOf(refunds = []) {
  const finals = (refunds ?? []).filter((r) => r?.final != null)
  return finals.length === 1 ? finals[0] : null
}

// The reply with the step's new amounts, every other word kept: the amount
// filled before (previous) swapped for the new final, the token filled, and
// the RMARTN breakdown lines set.
export function refilledReply(reply, { previous = null, refund = null } = {}) {
  if (!refund || refund.final == null) return reply
  let out = replaceRefundAmount(reply, previous, refund.final)
  if (hasRefundBreakdownLines(out)) out = setRefundAmountLines(out, { original: refund.original, deduction: refund.deduction, final: refund.final })
  return out
}

// The reply the step's outcome calls for on a Website order (the server's
// agent-rmartn.js stepReplyKind()): scrap is "Refund No Return"; a return the
// customer pays for is "RMARTN Cust Pays", one we pay for "Label Company
// Pays". null when not known.
export function replyKindFor(refund) {
  if (!refund?.disposition) return null
  if (refund.disposition === 'scrap') return 'refund_no_return'
  if (refund.payer === 'customer') return 'cust_pays'
  if (refund.payer === 'company') return 'company_pays'
  return null
}

// The outcome's reply the panel offers when the step's outcome changed: the
// step's params.replyByKind entry for the new kind, as a house reply
// ({ text, template }) for switchToHouseReply(), with the step's amounts
// filled. null when nothing changed or there is no reply for it.
export function outcomeReplyFor(action, refund, { lastKind = null } = {}) {
  const kind = replyKindFor(refund)
  if (!kind || kind === (lastKind ?? action?.params?.replyKind ?? null)) return null
  const v = action?.params?.replyByKind?.[kind]
  if (!v?.text) return null
  // noReturn / companyPays: the house switch's own flags (houseTemplateReply.js
  // houseReplyAction()), so a reply that keeps the RMA chip still follows a
  // step switched to scrap or a company-paid return.
  return {
    kind,
    house: {
      text: refilledReply(v.text, { previous: null, refund }),
      template: v.title ? { folder: null, title: v.title } : null,
      ...(kind === 'refund_no_return' ? { noReturn: true } : {}),
      ...(kind === 'company_pays' ? { companyPays: true } : {}),
    },
  }
}

// The Send guard's warnings (never blocking), with the fix where there is
// one: { code, index, message, fix: { amount, stepAmount } | null }.
// actions / inputs / unchecked (2026-10-30, Jenn): the items check
// (itemsWarning()) on the step's items as they are now.
export function sendWarnings(reply, refunds = [], { actions = null, inputs = {}, unchecked = [] } = {}) {
  const items = Array.isArray(actions) ? itemsWarning(reply, actions, { inputs, unchecked }) : null
  return [...replyStepWarnings({ reply, refunds }), ...(items ? [items] : [])].map((w) => ({
    ...w,
    fix: w.code === REPLY_STEP_WARNING_CODES.AMOUNT_NOT_ON_STEP && w.stepAmount != null ? { amount: w.amount, stepAmount: w.stepAmount } : null,
  }))
}

// The reply names items the step's items are not (2026-10-30, Jenn): a
// best-effort check of the reply's part words and positions against the one
// return, replacement or cancel step's items now (the person's selection,
// else the step's own). The warning names the step's items. null when it
// matches or there is no single such step.
export function itemsWarning(reply, actions = [], { inputs = {}, unchecked = [] } = {}) {
  const at = itemStepOf(actions, { unchecked })
  if (!at) return null
  const live = inputs?.[at.index]?.items
  const selected = Array.isArray(live) ? live : (at.a.itemSelection.defaultSelected ?? [])
  return replyItemsWarning({ reply, index: at.index, selected, allItems: at.a.itemSelection.allItems })
}

// The decision's record of the warnings Send went past: codes and step
// indexes only, never the reply's text or amounts.
export function sendWarningsRecord(warnings = []) {
  return (warnings ?? []).map((w) => ({ code: w.code, index: Number.isInteger(w.index) ? w.index : null }))
}

export const USE_STEP_AMOUNT_LABEL = "Use the step's amount"
export const FROM_PIPELINE_CHIP = 'From the pipeline'
